/**
 * Pure, framework-free core of <EntityPicker> (R4). Everything here is node-testable so the two things
 * that actually caused bugs across the six hand-rolled typeaheads — stale-response races and off-by-one
 * keyboard nav — are covered without a DOM. The React shell in EntityPicker.tsx is a thin wrapper over
 * these; its live behaviour is verified in the browser.
 */

/** A monotonic request gate: the fix for the stale-request race. Issue a token before each fetch and
 * only apply a response when its token is still the latest — an earlier, slower request can never
 * overwrite a newer one's results. (Three of the old typeaheads lacked this and could flash stale rows.) */
export interface RequestGate {
  /** Start a new request; returns its token (the newest). */
  issue: () => number;
  /** True only for the most recently issued token. */
  isLatest: (token: number) => boolean;
}

export function makeRequestGate(): RequestGate {
  let latest = 0; // 0 = no request issued yet
  return {
    issue: () => ++latest, // issued tokens are always >= 1
    isLatest: (token) => token !== 0 && token === latest,
  };
}

/** How many rows are navigable: the result rows plus the optional trailing «+ افزودن» create row. */
export function navigableCount(resultCount: number, hasCreateRow: boolean): number {
  return resultCount + (hasCreateRow ? 1 : 0);
}

/** Clamp an active-row move to [0, total-1]; no wrap (matches GlobalSearch). Returns 0 when empty. */
export function stepActive(current: number, delta: number, total: number): number {
  if (total <= 0) return 0;
  const next = current + delta;
  if (next < 0) return 0;
  if (next > total - 1) return total - 1;
  return next;
}

/** What pressing Enter on the active row means, given how many real results there are and whether a
 * create row is shown. The create row (when present) is always the last navigable index. */
export type EnterAction = { kind: 'select'; index: number } | { kind: 'create' } | { kind: 'none' };

export function resolveEnter(
  active: number,
  resultCount: number,
  hasCreateRow: boolean,
): EnterAction {
  if (active >= 0 && active < resultCount) return { kind: 'select', index: active };
  if (hasCreateRow && active === resultCount) return { kind: 'create' };
  return { kind: 'none' };
}

/** Build the GET url for the endpoint mode: `${endpoint}?${queryParam}=${q}` plus any extra params
 * (e.g. limit). Query text is always encoded; extra params are appended in insertion order. */
export function buildSearchUrl(
  endpoint: string,
  queryParam: string,
  query: string,
  extraParams?: Record<string, string | number>,
): string {
  const parts = [`${queryParam}=${encodeURIComponent(query)}`];
  if (extraParams) {
    for (const [k, v] of Object.entries(extraParams)) {
      parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
    }
  }
  return `${endpoint}?${parts.join('&')}`;
}
